import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Step, StepConnector, StepLabel, Stepper as MUIStepper } from '@mui/material';
import React, { Fragment, useCallback } from 'react';

export interface Step<T extends string> {
  label: string;
  step: T;
}

interface Props<T extends string> {
  activeStep: T;
  steps: Array<Step<T>>;
  skippedSteps?: Set<T>;
  labelOrientation?: 'horizontal' | 'vertical';
}

export function Stepper2<T extends string>({
  steps,
  activeStep,
  skippedSteps,
  labelOrientation = 'vertical',
}: Props<T>) {
  const isStepSkipped = useCallback((step: T) => skippedSteps?.has(step), [skippedSteps]);

  const activeStepIndex = steps.findIndex((step) => step.step === activeStep);

  const isCompleted = useCallback(
    (step: T) => {
      const indexForStep = steps.findIndex((s) => s.step === step);
      return !isStepSkipped(step) && activeStep && activeStepIndex > indexForStep;
    },
    [activeStep, activeStepIndex, isStepSkipped, steps]
  );

  return (
    <MUIStepper
      activeStep={activeStepIndex}
      alternativeLabel={labelOrientation === 'vertical'}
      connector={null}
    >
      {steps.map(({ label, step }, index) => {
        const completed = isCompleted(step);

        return (
          <Fragment key={label}>
            {index !== 0 && index < steps.length && (
              <StepConnector
                className={`${activeStep === step ? 'Mui-active' : ''} ${
                  completed ? 'Mui-completed' : ''
                }`}
              />
            )}
            <Step key={label} completed={completed}>
              <StepLabel
                sx={{ '& .MuiStepLabel-label': { marginTop: 0 } }}
                slots={{
                  stepIcon: () => (completed ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />),
                }}
                className={`${activeStep === step ? 'Mui-active' : ''} ${
                  completed ? 'Mui-completed' : ''
                }`}
              >
                {label}
              </StepLabel>
            </Step>
          </Fragment>
        );
      })}
    </MUIStepper>
  );
}
